// ec-rates-config.js — Single source of truth for tariffs, appliances and
// family presets. When BERC changes rates, this is the ONLY file that needs
// editing (see README "Rate update SOP").
//
// SOURCE: BERC "খুচরা বিদ্যুৎ মূল্যহার, ২০২৬", Gazette Order No. 2026/22,
//         dated 03 June 2026, effective the June 2026 billing cycle.
//         (Supersedes SRO 43-Law/2024 of 29 February 2024.)
// REFERENCE: https://berc.portal.gov.bd/
// LAST UPDATED: 2026-08-08
//
// ✅ VERIFIED against the gazette order (the PDF is in docs/sources/): the LT-A
// slab ladder, the all-or-nothing lifeline tier, the ৳42/kW demand charge and
// the 0.5% prepaid rebate on the net-of-VAT bill.
//
// ⚠️ NOT in the gazette: the 5% VAT (set by NBR, not BERC) and the meter rent
// (BERC defers it to each utility's "প্রচলিত নিয়ম"). Both stand on other
// evidence — see docs/RATE-SOURCES.md.
//
// The LT-A residential ladder is UNIFORM NATIONALLY — one identical table for
// all six distribution entities in a single order. The six company blocks below
// are therefore identical *by verification*, not as a placeholder.
//
// ⚠️ Appliance wattages are still indicative (typical BD market figures,
// partly from press listicles). See docs/APPLIANCE-WATTAGES.md.
//
// ✅ Run 5: every `recommend` tag is a REAL, verified Shopify collection
// handle (Anik checked each directly against the live AlphaEShop/TechBuyBD
// nav — not scraped or guessed). Earlier runs shipped five placeholder
// strings (`bldc-fan`, `inverter-ac`, `led-bulb`, `energy-star-fridge`,
// `water-heater`) that never existed as real collections; Phase 1 replaced
// them, and Phase 2 wired real collections onto ~40 more appliances that
// previously had no `recommend` at all. Full mapping + reasoning:
// docs/RECOMMEND-SOURCES.md.
//
// Loads in both the browser (window.EC_RATES) and Node (module.exports).

(function (root) {
  'use strict';

  // Savings factors for upgrades whose benefit is NOT a peak-wattage difference.
  // Declared here so each one has exactly one home; both are surfaced on
  // `common` below and referenced by the appliances that use them.

  // Inverter ACs save through compressor modulation over time, not a lower peak
  // rating. Modelling the saving as (1650W - 1400W) understates it badly and is
  // easy for an informed customer to dispute, so we model it as a monthly-kWh
  // reduction instead. Basis: docs/APPLIANCE-WATTAGES.md.
  var INVERTER_SAVINGS = 0.35;

  // An instant/on-demand heater avoids the standby reheat cycle that a storage
  // geyser pays for all day. Comparing peak watts (4500 vs 3000) would make the
  // upgrade look WORSE, which is why this one is a factor too.
  // ⚠️ ESTIMATE — see docs/APPLIANCE-WATTAGES.md, needs verification before ship.
  var INSTANT_WH_SAVINGS = 0.30;

  // Run 4 Phase 3a: a custom appliance (typed in by the user under "+ Add your
  // own appliance") has no catalog `recommend`/`recommendTo` of its own —
  // there is nothing to look up. Rather than force a bad match,
  // ec-appliances.js falls back to AT MOST one generic recommendation per
  // category, read from this map.
  //
  // Run 5 Phase 1: tags updated from the old placeholder handles to the real,
  // verified AlphaEShop collection handles — see docs/RECOMMEND-SOURCES.md.
  // `recommendTo` still names a concrete appliance where the fallback is a
  // genuine before/after savings claim (cooling, lighting — unchanged
  // reasoning below). `store` says which storefront the tag lives on.
  //
  // Run 5 Phase 2 adds a THIRD fallback, personal care -> beautyaccessories,
  // with `recommendTo: null` — there is no in-catalog "efficient hair dryer"
  // to compute a saving against, so this one is a plain cross-sell (see the
  // `recommendModel: 'crosssell'` path in ec-recommend.js): it still shows a
  // real AlphaEShop product, just with no savings/payback claim attached.
  //
  // Only cooling and lighting carry a genuine savings pairing, and only
  // because every device in those two categories is functionally
  // interchangeable with the fallback target: any lighting device can
  // genuinely be replaced by an LED bulb, and "a fan" is the closest safe
  // generic guess for "a cooling device" — a custom ceiling/table/pedestal/
  // wall fan really can become a BLDC fan.
  //
  // ⚠️ DELIBERATE APPROXIMATION, not a bug: this still produces a
  // technically-computed "switch to a BLDC fan" suggestion for a custom
  // COOLING entry that is actually an AC or air cooler, which cannot
  // functionally be replaced by a fan even though the math says it saves
  // power. No per-device disambiguation exists for a free-text custom entry.
  // See docs/APPLIANCE-WATTAGES.md and docs/RATE-SOURCES.md.
  //
  // Kitchen and entertainment span devices with no honest single generic
  // match — a custom "kitchen" entry could be a blender or an oven, and
  // nothing sold in one collection helps both — so those stay absent, and a
  // custom appliance in them gets no recommendation at all rather than a
  // wrong one.
  var CATEGORY_FALLBACK_RECOMMEND = {
    cooling: { tag: 'ceiling-fan', recommendTo: 'fan_bldc', store: 'alphaeshop' },
    lighting: { tag: 'all-lights', recommendTo: 'led_9', store: 'alphaeshop' },
    personal: { tag: 'beautyaccessories', recommendTo: null, store: 'alphaeshop' }
  };

  /**
   * The LT-A (আবাসিক / residential) tariff ladder — BERC Order 2026/22.
   *
   * ✅ VERIFIED against the gazette. This ONE table is the tariff for every
   * distribution entity: BERC sets a single national residential ladder, so
   * DESCO, DPDC, NESCO, WZPDCL, BPDB and BREB all bill from these numbers.
   *
   * A factory, not a shared constant: each company gets an independent copy, so
   * a future per-company divergence can be applied to one block without any
   * chance of silently moving the other five.
   *
   * LIFELINE IS ALL-OR-NOTHING (verified): at ≤50 units the whole consumption
   * bills at ৳5.32; at 51 units the lifeline rate does not apply at all and
   * every unit flows through `slabs` from the bottom. The 266.00 → 315.18 jump
   * at that boundary is the gazette's behaviour, not a bug.
   */
  function ltaResidential() {
    return {
      label: { en: 'Residential', bn: 'আবাসিক' },
      lifeline: { threshold: 50, rate: 5.32 },
      slabs: [
        { min: 0, max: 75, rate: 6.18 },
        { min: 76, max: 200, rate: 8.50 },
        { min: 201, max: 300, rate: 9.10 },
        { min: 301, max: 400, rate: 9.62 },
        { min: 401, max: 600, rate: 15.01 },
        // The top tier is open-ended. `Infinity` is not JSON-serialisable and
        // must never reach the UI as a number — ec-core sizes this slab from the
        // remaining units and ec-results renders it as "৬০১+".
        { min: 601, max: Infinity, rate: 17.35 }
      ]
    };
  }

  var EC_RATES = {
    version: '2026-06',

    // See the CATEGORY_FALLBACK_RECOMMEND comment above.
    categoryFallbackRecommend: CATEGORY_FALLBACK_RECOMMEND,

    common: {
      // ⚠️ 5% VAT. The rate is set by NBR, not BERC, so it appears in neither
      // gazette; it is carried forward from SRO 43-Law/2024 practice.
      //
      // BASE: energy + demand + meter rent. Widened in this pass — the previous
      // base omitted meter rent and under-stated VAT on every real bill.
      // Four WZPDCL prepaid vending receipts all satisfy VAT = vending ÷ 21
      // exactly, i.e. 5% levied VAT-inclusive on (energy + demand + meterRent)
      // net of rebate. We keep the simpler EXCLUSIVE method (5% added on top of
      // the pre-VAT subtotal) — the difference is a few paisa and the exclusive
      // form is what a customer can check by hand. See docs/RATE-SOURCES.md.
      vat: 0.05,

      // ✅ VERIFIED verbatim — BERC Order 2026/22 clause ৪ (and SRO 43-Law/2024
      // clause ৯(ক), identical wording): "প্রি-পেইড মিটার গ্রাহকগণ ... মূল্য
      // সংযোজন কর ব্যতীত নীট বিলের ওপর ০.৫% ... হারে রিবেট প্রাপ্ত হবেন".
      //
      // BASE: energy + demand — NOT including meter rent. The gazette phrase
      // "net bill excluding VAT" is ambiguous about meter rent; the four real
      // receipts resolve it, and they reconcile on the energy+demand base.
      // The two readings differ by ৳0.20/month, so this is a precision choice,
      // not a material one. See meterRent below and docs/RATE-SOURCES.md.
      prepaidRebate: 0.005,

      // ⚠️ METER RENT — a fixed monthly charge that neither gazette prices.
      //
      // BERC Order 2026/22 and SRO 43-Law/2024 clause ৮(ক) both say only that
      // "মিটার ভাড়া সংক্রান্ত প্রচলিত নিয়ম অব্যাহত থাকিবে" — the prevailing
      // rule continues. The NUMBER is therefore set by each distribution
      // utility, not by the regulator, and this ৳40 is read off four real
      // WZPDCL prepaid vending receipts (meter 050350147924, 4 kW, single
      // phase), not off a published tariff.
      //
      // It is charged ONCE PER CALENDAR MONTH: it appears on the month's first
      // recharge and shows 0 on every later recharge in the same month. This
      // calculator models exactly one month, so it is included exactly once.
      //
      // ⚠️ CAVEAT worth knowing before quoting it: clause ৮(খ) waives meter
      // rent entirely for customers who paid the meter + installation cost
      // up-front or supplied their own standard meter. Those households owe
      // ৳0 here. The calculator has no control for that (see PROGRESS.md).
      meterRent: 40,

      // ⚠️ UNKNOWN — deliberately null rather than guessed. No three-phase
      // receipt was available, and the gazette does not price meter rent at
      // all, so there is nothing to read it off. ec-core treats null as
      // "unknown" and bills ৳0 while flagging the bill, so a wrong number can
      // never reach a customer. LT-A residential is single-phase in practice.
      meterRentThreePhase: null,

      // ✅ There is NO on-time rebate in the 2026 order; the earlier config
      // carried one and it did not exist. What the order does define is a
      // one-time 5% LATE-PAYMENT SURCHARGE on a bill not settled by its due
      // date. On-time is therefore the plain, unadjusted bill.
      latePaymentSurcharge: 0.05,

      demandChargePerKW: { 'LT-A': 42 },

      // Slab-crossing alert threshold: the zero-based index into a category's
      // `slabs` array at which the Step 2 warning toast starts firing.
      //
      // Crossing into the cheap lower tiers is not news and not a reason to
      // alarm anyone — a household moving from ৳6.18 to ৳8.50 has not hit a
      // problem worth a red toast, and warning on every tier trains the user to
      // ignore the one that matters. Index 3 is the 301-400 tier, which is the
      // jump PROJECT.md §3 calls out and the first one where an efficiency
      // upgrade clearly pays for itself.
      //
      // Index map for LT-A (2026): 0=৳6.18 1=৳8.50 2=৳9.10 3=৳9.62 4=৳15.01 5=৳17.35
      //
      // NOTE (2026 tariff): under the new ladder the steepest cliff is no longer
      // index 3 (৳9.10 → ৳9.62, +5.7%) but index 4 (৳9.62 → ৳15.01, +56%).
      // The threshold is left at 3 because 301+ units is still the household
      // PROJECT.md §3 targets and warning earlier is the safer error, but this
      // is worth revisiting against real funnel data.
      slabAlertFromIndex: 3,

      // See the constants at the top of this file for the reasoning.
      inverterSavingsFactor: INVERTER_SAVINGS,
      instantWaterHeaterSavingsFactor: INSTANT_WH_SAVINGS,

      // Longest payback we are willing to put in front of a customer, in months.
      //
      // Some upgrades genuinely do not pay for themselves: a ৳48,000 efficient
      // fridge saving ৳199/month takes 20 years, and its 10-year net is
      // NEGATIVE. Showing that card would be selling someone a loss, and
      // DESIGN.md 1.3 is explicit that every recommendation carries a real
      // payback number. Recommendations above this threshold are dropped
      // entirely rather than shown with an embarrassing figure.
      //
      // 60 months = 5 years. Raise it to be more aggressive, lower it to be
      // more conservative.
      maxPaybackMonths: 60
    },

    // =======================================================================
    // Run 4 Phase 4 — the full BERC LT customer-class taxonomy, for the Step 1
    // dropdown. ⚠️ PLACEHOLDER LIST, not a rate engine: only 'LT-A' has actual
    // tariff data anywhere in this file (see `companies` below). The other
    // eight are real BERC classes — shown so the tool doesn't look like it
    // only serves residential customers — but selecting one is a dead end by
    // design: EC.getCategory(company, class) returns null for anything but
    // 'LT-A', and every module that would otherwise compute a bill (the
    // pinned meter, Step 3 results, the report card) checks that first and
    // shows a "coming soon" state instead of guessing at a flat/off-peak/peak
    // rate structure this run deliberately does not implement. See
    // PROGRESS.md Phase 4 and DESIGN.md.
    //
    // The taxonomy itself (which class covers what) is a national BERC
    // definition, not a per-company one, so — like the LT-A ladder — it is
    // the same list for all six distribution companies.
    customerClasses: [
      { key: 'LT-A', en: 'Residential', bn: 'আবাসিক' },
      { key: 'LT-B', en: 'Irrigation/Agriculture Pump', bn: 'সেচ/কৃষি পাম্প' },
      { key: 'LT-C1', en: 'Small Industry', bn: 'ক্ষুদ্র শিল্প' },
      { key: 'LT-C2', en: 'Construction', bn: 'নির্মাণ' },
      { key: 'LT-D1', en: 'Education/Religious/Charitable/Hospital', bn: 'শিক্ষা/ধর্মীয়/দাতব্য/হাসপাতাল' },
      { key: 'LT-D2', en: 'Street Light/Water Pump', bn: 'সড়ক বাতি/পানির পাম্প' },
      { key: 'LT-D3', en: 'EV/Battery Charging Station', bn: 'ইভি/ব্যাটারি চার্জিং স্টেশন' },
      { key: 'LT-E', en: 'Commercial/Office', bn: 'বাণিজ্যিক/অফিস' },
      { key: 'LT-I', en: 'Temporary', bn: 'সাময়িক' }
    ],

    // =======================================================================
    // ✅ VERIFIED — BERC Order 2026/22 publishes ONE LT-A residential ladder
    // that applies to every distribution entity. There is no per-company
    // variation to find; the six blocks below are identical because the gazette
    // makes them identical, not because they are placeholders awaiting real
    // numbers. (The earlier scaffold's central open question — "is the ladder
    // uniform or per-company?" — is now answered: uniform.)
    //
    // ltaResidential() hands each company its OWN copy of the table rather than
    // a shared reference, so the day BERC does split the ladder, one company can
    // be given its own figures without touching the other five.
    // =======================================================================
    companies: {
      DESCO:  { name: { en: 'DESCO',  bn: 'ডেসকো' },   categories: { 'LT-A': ltaResidential() } },
      DPDC:   { name: { en: 'DPDC',   bn: 'ডিপিডিসি' }, categories: { 'LT-A': ltaResidential() } },
      NESCO:  { name: { en: 'NESCO',  bn: 'নেসকো' },   categories: { 'LT-A': ltaResidential() } },
      BPDB:   { name: { en: 'BPDB',   bn: 'বিপিডিবি' }, categories: { 'LT-A': ltaResidential() } },
      WZPDCL: { name: { en: 'WZPDCL', bn: 'ওজোপাডিকো' }, categories: { 'LT-A': ltaResidential() } },
      // পল্লী বিদ্যুৎ — the rural samitis. Checked specifically, because this is
      // the one that would most plausibly differ: it does not. Same LT-A ladder.
      BREB:   { name: { en: 'BREB (Palli Bidyut)', bn: 'পল্লী বিদ্যুৎ (বিআরইবি)' }, categories: { 'LT-A': ltaResidential() } }
    },

    // =======================================================================
    // Step 1 distributor picker — the plain <select> became a row of logo
    // chips. This array is ONLY display order + which logo file belongs to
    // which company; the bilingual display name (used for the chip's
    // accessible label) stays on `companies[id].name` above so there is
    // exactly one place that can go stale. ec-wizard.js builds the radio
    // group from this list and falls back to Object.keys(companies) if it
    // were ever left empty.
    //
    // Logo files live in assets/ec-logo-{id}.png — mixed native shapes (round
    // badges, wordmarks, one solid-fill square), normalised to one visual
    // size by the fixed tile ec-styles.css draws around each.
    // =======================================================================
    distributors: [
      { id: 'DESCO',  logo: 'ec-logo-desco.png' },
      { id: 'DPDC',   logo: 'ec-logo-dpdc.png' },
      { id: 'NESCO',  logo: 'ec-logo-nesco.png' },
      { id: 'WZPDCL', logo: 'ec-logo-wzpdcl.png' },
      { id: 'BPDB',   logo: 'ec-logo-bpdb.png' },
      { id: 'BREB',   logo: 'ec-logo-breb.png' }
    ],

    // group | key | bn/en label | watts | dutyCycle? | recommend?
    //
    // `watts` is the rated input power. `dutyCycle` (optional, defaults to 1) is
    // the fraction of the user-entered hours during which the appliance actually
    // draws that power — thermostatically cycling appliances never run flat out.
    // Users enter intuitive hours (a fridge is "on" 24h); the duty cycle turns
    // that into realistic consumption. Reasoning per value is in
    // docs/APPLIANCE-WATTAGES.md.
    //
    // `recommend` is the real collection/tag on `store` ('alphaeshop' by
    // default, or 'techbuybd') that ec-recommend.js fetches/links to.
    //
    // Recommendation modelling (ec-recommend.js reads these):
    //   recommendTo     — the appliance key the user would move TO. The engine
    //                     reads that entry's watts + dutyCycle, so the upgrade
    //                     target is real data, never a number typed twice.
    //                     OMITTED entirely on ~40 Run 5 Phase 2 entries that
    //                     have no efficient in-catalog counterpart to compare
    //                     against (a router, a rice cooker, a hair dryer) —
    //                     those recommend a real product with no savings
    //                     claim attached. See recommendModel: 'crosssell'.
    //   recommendModel  — 'watts' (default): recompute units at the target's
    //                     wattage. 'factor': the benefit is not a peak-wattage
    //                     difference, so reduce monthly units by
    //                     `recommendFactor` instead — recommendTo is then used
    //                     only for its label. 'crosssell': implied whenever
    //                     `recommend` is set but `recommendTo` is not — no
    //                     units/savings/payback math is computed or shown
    //                     (DESIGN.md hard-don't #2 forbids inventing a payback
    //                     number), just a real product pointed at a real
    //                     customer need. See docs/RECOMMEND-SOURCES.md for the
    //                     full reasoning and the complete tag table.
    //   store           — 'alphaeshop' (same-origin product fetch + real
    //                     add-to-cart, unchanged Run 1-4 mechanism) or
    //                     'techbuybd' (a separate Shopify store; no
    //                     cross-origin product fetch, no cart integration —
    //                     ec-recommend.js renders a static "View on
    //                     TechBuyBD" link card instead). Defaults to
    //                     'alphaeshop' when omitted.
    //   brandRecommendations — optional [{ minWatts, brands: [{ name, avgW }] }].
    //                     When the user's OWN entered wattage for this appliance
    //                     is at or above `minWatts`, ec-recommend.js names those
    //                     brands on the recommendation card and ec-card.js
    //                     surfaces them in the report card's
    //                     savings-opportunity row. Pure signposting — NO
    //                     savings/payback figure is ever derived from it, so
    //                     DESIGN.md hard-don't #2 does not apply. `brands` is
    //                     shown in list order (a merchandising choice, not
    //                     sorted by wattage). Added for the 80W+ ceiling-fan
    //                     case: these BLDC brands draw a fraction of a
    //                     conventional 80-100W induction-motor fan.
    appliances: [
      // --- ঠান্ডা ও বাতাস ---------------------------------------------------
      { group: 'cooling',       key: 'fan_regular',   bn: 'সিলিং ফ্যান',              en: 'Ceiling fan',          watts: 75,   recommend: 'ceiling-fan', recommendTo: 'fan_bldc', store: 'alphaeshop', brandRecommendations: [{ minWatts: 80, brands: [{ name: 'Qulik', avgW: 43 }, { name: 'Pakhtun', avgW: 40 }] }] },
      // Crosssell: already the efficient endpoint of the pair above, so no
      // recommendTo — just points at AlphaEShop's own BLDC-fan collection.
      { group: 'cooling',       key: 'fan_bldc',      bn: 'BLDC ফ্যান',               en: 'BLDC fan',             watts: 32,   recommend: 'energy-savings-bldc-fan', store: 'alphaeshop' },
      { group: 'cooling',       key: 'fan_table',     bn: 'টেবিল ফ্যান',              en: 'Table fan',            watts: 55,   recommend: 'stand-fan', store: 'alphaeshop' },
      { group: 'cooling',       key: 'fan_exhaust',   bn: 'এক্সহস্ট ফ্যান',           en: 'Exhaust fan',          watts: 30,   recommend: 'wall-fan', store: 'alphaeshop' },
      { group: 'cooling',       key: 'ac_noninv_1_0', bn: 'AC 1 টন (নন-ইনভার্টার)',   en: 'AC 1T non-inverter',   watts: 1200, recommend: 'air-conditioner', recommendTo: 'ac_inv_1_0', recommendModel: 'factor', recommendFactor: INVERTER_SAVINGS, store: 'alphaeshop' },
      { group: 'cooling',       key: 'ac_inv_1_0',    bn: 'AC 1 টন (ইনভার্টার)',      en: 'AC 1T inverter',       watts: 1000, recommend: 'air-conditioner', store: 'alphaeshop' },
      { group: 'cooling',       key: 'ac_noninv_1_5', bn: 'AC 1.5 টন (নন-ইনভার্টার)', en: 'AC 1.5T non-inverter', watts: 1650, recommend: 'air-conditioner', recommendTo: 'ac_inv_1_5', recommendModel: 'factor', recommendFactor: INVERTER_SAVINGS, store: 'alphaeshop' },
      { group: 'cooling',       key: 'ac_inv_1_5',    bn: 'AC 1.5 টন (ইনভার্টার)',    en: 'AC 1.5T inverter',     watts: 1400, recommend: 'air-conditioner', store: 'alphaeshop' },
      { group: 'cooling',       key: 'air_cooler',    bn: 'এয়ার কুলার',               en: 'Air cooler',           watts: 200,  recommend: 'air-conditioner', store: 'alphaeshop' },
      // ⚠️ ESTIMATED — Run 4 Phase 3b additions, see docs/APPLIANCE-WATTAGES.md.
      { group: 'cooling',       key: 'fan_pedestal',  bn: 'পেডেস্টাল ফ্যান',          en: 'Pedestal fan',         watts: 65,   recommend: 'stand-fan', store: 'alphaeshop' },
      { group: 'cooling',       key: 'fan_wall',      bn: 'দেয়াল ফ্যান',              en: 'Wall fan',             watts: 65,   recommend: 'wall-fan', store: 'alphaeshop' },
      // Battery-backed stand fan — a load-shedding staple, not a duplicate of
      // fan_bldc (that's a mains ceiling fan upgrade target). Not named in the
      // Run 5 mapping table — left unwired, see docs/RECOMMEND-SOURCES.md.
      { group: 'cooling',       key: 'fan_rechargeable', bn: 'রিচার্জেবল ফ্যান',      en: 'Rechargeable fan',     watts: 50 },

      // --- আলো ---------------------------------------------------------------
      { group: 'lighting',      key: 'led_9',         bn: 'LED বাল্ব 9W',             en: 'LED 9W',               watts: 9,    recommend: 'all-lights', store: 'alphaeshop' },
      { group: 'lighting',      key: 'led_18',        bn: 'LED বাল্ব 18W',            en: 'LED 18W',              watts: 18,   recommend: 'all-lights', store: 'alphaeshop' },
      { group: 'lighting',      key: 'tube_light',    bn: 'টিউব লাইট',                en: 'Tube light',           watts: 40,   recommend: 'all-lights', recommendTo: 'led_18', store: 'alphaeshop' },
      { group: 'lighting',      key: 'cfl_20',        bn: 'CFL বাল্ব',                en: 'CFL 20W',              watts: 20,   recommend: 'all-lights', recommendTo: 'led_9', store: 'alphaeshop' },
      { group: 'lighting',      key: 'incand_60',     bn: 'সাধারণ বাল্ব',             en: 'Incandescent',         watts: 60,   recommend: 'all-lights', recommendTo: 'led_9', store: 'alphaeshop' },
      // ⚠️ ESTIMATED — Run 4 Phase 3b additions, see docs/APPLIANCE-WATTAGES.md.
      { group: 'lighting',      key: 'led_strip',     bn: 'LED স্ট্রিপ লাইট',         en: 'LED strip light',      watts: 15,   recommend: 'all-lights', store: 'alphaeshop' },
      { group: 'lighting',      key: 'lamp_table',    bn: 'টেবিল ল্যাম্প',            en: 'Table lamp',           watts: 15,   recommend: 'table-lamps', store: 'alphaeshop' },
      // Not named in the Run 5 mapping table — left unwired rather than guessed.
      { group: 'lighting',      key: 'light_emergency', bn: 'রিচার্জেবল এমার্জেন্সি লাইট', en: 'Rechargeable emergency light', watts: 5 },
      { group: 'lighting',      key: 'led_panel',     bn: 'LED প্যানেল লাইট',         en: 'LED panel light',      watts: 18 },
      { group: 'lighting',      key: 'led_flood',     bn: 'LED ফ্লাড লাইট',           en: 'LED flood light',      watts: 50 },
      { group: 'lighting',      key: 'light_lantern', bn: 'রিচার্জেবল লণ্ঠন',         en: 'Rechargeable lantern', watts: 3 },

      // --- রান্নাঘর ------------------------------------------------------------
      // dutyCycle 0.35 — compressor cycles against the thermostat; entered as 24h.
      // Run 5 Phase 1: recommend tag is 'dish-washer', NOT a fridge-specific
      // collection — AlphaEShop has no dedicated efficient-fridge collection
      // yet, so the fridge upgrade story (real savings math, fridge_star
      // target) shares a product pool with the dishwasher/washer cross-sell
      // entries below. See docs/RECOMMEND-SOURCES.md — flagged as worth
      // revisiting once AlphaEShop stocks a dedicated line.
      { group: 'kitchen',       key: 'fridge',        bn: 'ফ্রিজ',                    en: 'Fridge',               watts: 150,  dutyCycle: 0.35, recommend: 'dish-washer', recommendTo: 'fridge_star', store: 'alphaeshop' },
      // dutyCycle 0.40 — a deep freezer holds a colder setpoint than a fridge, so
      // its compressor runs a larger share of the time.
      { group: 'kitchen',       key: 'deep_freezer',  bn: 'ডিপ ফ্রিজ',                en: 'Deep freezer',         watts: 200,  dutyCycle: 0.40, recommend: 'dish-washer', recommendTo: 'freezer_star', store: 'alphaeshop' },
      // The two upgrade targets above. Selectable in their own right — a user who
      // already owns an efficient unit must be able to say so. Not named as
      // their own row in the Run 5 table — left unwired rather than guessed.
      // ⚠️ ESTIMATES: an inverter-compressor unit both draws less and cycles less
      // than an old one. Both figures need checking against the actual models
      // AlphaEShop stocks — see docs/APPLIANCE-WATTAGES.md.
      { group: 'kitchen',       key: 'fridge_star',   bn: 'এনার্জি সেভিং ফ্রিজ',      en: 'Energy-star fridge',   watts: 100,  dutyCycle: 0.30 },
      { group: 'kitchen',       key: 'freezer_star',  bn: 'এনার্জি সেভিং ডিপ ফ্রিজ',  en: 'Energy-star freezer',  watts: 150,  dutyCycle: 0.35 },
      // dutyCycle 0.30 — storage geyser reheats intermittently while switched on.
      { group: 'kitchen',       key: 'geyser',        bn: 'গিজার (স্টোরেজ)',          en: 'Geyser (storage)',     watts: 3000, dutyCycle: 0.30, recommend: 'water-heater', recommendTo: 'instant_geyser', recommendModel: 'factor', recommendFactor: INSTANT_WH_SAVINGS, store: 'alphaeshop' },
      // No dutyCycle: an instant geyser draws full power only while the tap runs,
      // and that IS the time the user enters. It is also the upgrade target
      // above — crosssell only, no recommendTo of its own.
      { group: 'kitchen',       key: 'instant_geyser', bn: 'ইনস্ট্যান্ট গিজার',       en: 'Instant geyser',       watts: 4500, recommend: 'water-heater', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'induction',     bn: 'ইনডাকশন চুলা',             en: 'Induction cooker',     watts: 2100, recommend: 'induction-cooker', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'rice_cooker',   bn: 'রাইস কুকার',               en: 'Rice cooker',          watts: 700,  recommend: 'rice-cooker', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'microwave',     bn: 'মাইক্রোওয়েভ',              en: 'Microwave',            watts: 2100, recommend: 'electric-gas-oven', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'oven',          bn: 'ইলেকট্রিক ওভেন',           en: 'Electric oven',        watts: 1500, recommend: 'electric-gas-oven', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'blender',       bn: 'ব্লেন্ডার',                en: 'Blender',              watts: 400,  recommend: 'mixer-grinder', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'kettle',        bn: 'ইলেকট্রিক কেটলি',          en: 'Electric kettle',      watts: 2200, recommend: 'electric-kettle', store: 'alphaeshop' },
      // ⚠️ ESTIMATED — Run 4 Phase 3b additions, see docs/APPLIANCE-WATTAGES.md.
      { group: 'kitchen',       key: 'otg',           bn: 'ওটিজি (ওভেন টোস্টার গ্রিলার)', en: 'OTG (oven toaster griller)', watts: 1200, recommend: 'electric-gas-oven', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'air_fryer',     bn: 'এয়ার ফ্রায়ার',             en: 'Air fryer',            watts: 1500, recommend: 'electric-gas-oven', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'toaster',       bn: 'টোস্টার',                  en: 'Toaster',              watts: 800,  recommend: 'electric-gas-oven', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'water_purifier', bn: 'ওয়াটার পিউরিফায়ার',      en: 'Water purifier/dispenser', watts: 30, recommend: 'water-purifier', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'dishwasher',    bn: 'ডিশওয়াশার',                en: 'Dishwasher',           watts: 1800, recommend: 'dish-washer', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'pressure_cooker_electric', bn: 'ইলেকট্রিক প্রেশার কুকার', en: 'Electric pressure cooker', watts: 1000, recommend: 'rice-cooker', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'sandwich_maker', bn: 'স্যান্ডউইচ মেকার',        en: 'Sandwich maker',       watts: 750,  recommend: 'electric-gas-oven', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'juicer',        bn: 'জুসার',                    en: 'Juicer',               watts: 400,  recommend: 'mixer-grinder', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'food_processor', bn: 'ফুড প্রসেসর',             en: 'Food processor',       watts: 600,  recommend: 'mixer-grinder', store: 'alphaeshop' },
      { group: 'kitchen',       key: 'hand_mixer',    bn: 'হ্যান্ড মিক্সার',          en: 'Hand mixer',           watts: 200,  recommend: 'hand-mixer', store: 'alphaeshop' },
      // Not named in the Run 5 mapping table — left unwired rather than guessed.
      { group: 'kitchen',       key: 'slow_cooker',   bn: 'স্লো কুকার',               en: 'Slow cooker',          watts: 200 },
      { group: 'kitchen',       key: 'coffee_maker',  bn: 'কফি মেকার',                en: 'Coffee maker',         watts: 800 },

      // --- বিনোদন -------------------------------------------------------------
      { group: 'entertainment', key: 'tv_led',        bn: 'LED TV',                   en: 'LED TV',               watts: 80,  recommend: 'monitor', store: 'techbuybd' },
      // The one crossover pair: CRT (120W) -> LED TV (80W) is a genuine,
      // already-in-catalog saving, so this is wired as a real savings pair
      // (not crosssell) even though the target lives on TechBuyBD — the
      // "saves ৳X/month" figure is real, only the price/payback is skipped
      // (Phase 2: no product is ever fetched from a second store).
      { group: 'entertainment', key: 'tv_crt',        bn: 'পুরনো CRT TV',             en: 'CRT TV',               watts: 120, recommend: 'monitor', recommendTo: 'tv_led', store: 'techbuybd' },
      { group: 'entertainment', key: 'desktop_pc',    bn: 'ডেস্কটপ কম্পিউটার',        en: 'Desktop PC',           watts: 200, recommend: 'gaming', store: 'techbuybd' },
      { group: 'entertainment', key: 'router',        bn: 'রাউটার',                   en: 'Router',               watts: 12,  recommend: 'router', store: 'techbuybd' },
      // ⚠️ ESTIMATED — Run 4 Phase 3b additions, see docs/APPLIANCE-WATTAGES.md.
      { group: 'entertainment', key: 'laptop',        bn: 'ল্যাপটপ ও চার্জার',        en: 'Laptop + charger',     watts: 65,  recommend: 'laptop', store: 'techbuybd' },
      // Not named in the Run 5 mapping table — left unwired rather than guessed.
      { group: 'entertainment', key: 'soundbar',      bn: 'সাউন্ডবার/স্পিকার',        en: 'Soundbar/speaker',     watts: 30 },
      { group: 'entertainment', key: 'gaming_console', bn: 'গেমিং কনসোল',             en: 'Gaming console',       watts: 150, recommend: 'gaming', store: 'techbuybd' },
      // Explicitly on the Run 5 "no recommendation" list.
      { group: 'entertainment', key: 'set_top_box',   bn: 'সেট-টপ বক্স',              en: 'Set-top box',          watts: 15 },
      // The catalog KEY here (a PC monitor peripheral) is unrelated to the
      // TechBuyBD 'monitor' TAG above (their TV collection handle) — a naming
      // coincidence, not wired to it. Not named in the mapping table.
      { group: 'entertainment', key: 'monitor',       bn: 'মনিটর',                    en: 'Monitor',              watts: 30 },
      { group: 'entertainment', key: 'projector',     bn: 'প্রজেক্টর',                en: 'Projector',            watts: 250 },

      // --- পানি ও গোসল ---------------------------------------------------------
      // Explicitly on the Run 5 "no recommendation" list.
      { group: 'water',         key: 'pump_1hp',      bn: 'পানির পাম্প 1 HP',         en: 'Pump 1HP',             watts: 746 },
      { group: 'water',         key: 'pump_05hp',     bn: 'পানির পাম্প 0.5 HP',       en: 'Pump 0.5HP',           watts: 373 },
      { group: 'water',         key: 'washer',        bn: 'ওয়াশিং মেশিন',             en: 'Washing machine',      watts: 500, recommend: 'dish-washer', store: 'alphaeshop' },
      // ⚠️ ESTIMATED — Run 4 Phase 3b additions, see docs/APPLIANCE-WATTAGES.md.
      { group: 'water',         key: 'pump_submersible', bn: 'সাবমার্সিবল পাম্প',      en: 'Submersible pump',     watts: 750 },
      // Not named in the Run 5 mapping table (a bucket-heating rod is a
      // distinct product from a geyser) — left unwired rather than guessed.
      { group: 'water',         key: 'water_heater_rod', bn: 'ইমারশন ওয়াটার হিটার',   en: 'Immersion water heater rod', watts: 1000 },

      // --- ব্যক্তিগত যত্ন (Run 4 Phase 3b — NEW category) ----------------------
      // hair_dryer moved here from "অন্যান্য" now that a proper home exists for
      // it; key and watts unchanged so no existing selection breaks.
      // 2200W — one of the "quietly bigger than your AC" devices PROJECT.md §3.2
      // builds the mythbuster around.
      { group: 'personal',      key: 'hair_dryer',    bn: 'হেয়ার ড্রায়ার',            en: 'Hair dryer',           watts: 2200, recommend: 'beautyaccessories', store: 'alphaeshop' },
      // ⚠️ ESTIMATED — Run 4 Phase 3b additions, see docs/APPLIANCE-WATTAGES.md.
      { group: 'personal',      key: 'hair_straightener', bn: 'হেয়ার স্ট্রেইটনার',    en: 'Hair straightener',    watts: 40,   recommend: 'beautyaccessories', store: 'alphaeshop' },
      { group: 'personal',      key: 'shaver_electric', bn: 'ইলেকট্রিক শেভার',        en: 'Electric shaver',      watts: 15,   recommend: 'beautyaccessories', store: 'alphaeshop' },
      { group: 'personal',      key: 'toothbrush_electric', bn: 'ইলেকট্রিক টুথব্রাশ', en: 'Electric toothbrush',  watts: 4,    recommend: 'beautyaccessories', store: 'alphaeshop' },
      // Not named in the Run 5 mapping table — left unwired rather than guessed.
      { group: 'personal',      key: 'hair_clipper',  bn: 'হেয়ার ক্লিপার/ট্রিমার',    en: 'Hair clipper/trimmer', watts: 10 },
      { group: 'personal',      key: 'massager',      bn: 'ম্যাসাজার',                en: 'Massager',             watts: 25 },
      { group: 'personal',      key: 'hair_curler',   bn: 'হেয়ার কার্লার',            en: 'Hair curler',          watts: 35 },

      // --- পরিষ্কার ও অন্যান্য (Run 4 Phase 3b — extends আগের "অন্যান্য") ------
      { group: 'other',         key: 'iron',          bn: 'ইস্ত্রি',                  en: 'Iron',                 watts: 1000, recommend: 'iron', store: 'alphaeshop' },
      // Explicitly on the Run 5 "no recommendation" list.
      { group: 'other',         key: 'vacuum_cleaner', bn: 'ভ্যাকুয়াম ক্লিনার',        en: 'Vacuum cleaner',       watts: 1400 },
      { group: 'other',         key: 'cctv_nvr',      bn: 'সিসিটিভি/এনভিআর সিস্টেম',  en: 'CCTV/NVR system',      watts: 40,   recommend: 'cc-camera', store: 'techbuybd' },
      // Not named in the Run 5 mapping table — left unwired rather than guessed.
      { group: 'other',         key: 'air_purifier',  bn: 'এয়ার পিউরিফায়ার',          en: 'Air purifier',         watts: 50 },
      { group: 'other',         key: 'sewing_machine', bn: 'সেলাই মেশিন (ইলেকট্রিক)', en: 'Electric sewing machine', watts: 100 },
      { group: 'other',         key: 'room_heater',   bn: 'রুম হিটার',                en: 'Room heater',          watts: 1500 }
    ],

    // Run 4 Phase 5: the 2-3 tiles each category shows BY DEFAULT — everything
    // else in that category is reachable through its "+ Add more appliances"
    // search panel instead of rendering all ~75 tiles up front. This is what
    // actually shortens Step 2; see PROGRESS.md Phase 5 for the measured delta.
    //
    // Fixed per category, NOT recomputed per family size — a stable set of
    // "common" tiles regardless of which preset is active reads as a
    // considered curation, not a UI that reshuffles itself under the user.
    // Every appliance any familyPreset below actually selects IS included
    // here for its category, so a preset's picks are never hidden behind the
    // search panel on first load. The rest is this run's own BD-household
    // judgement, same as the Phase 3b catalog expansion — the appliance most
    // households in that category would reach for first.
    //
    // A tile also stays visible once selected regardless of this list (see
    // ec-appliances.js buildCategory()), so promoting something from the
    // search panel — or restoring a saved session — never un-shows it again.
    commonAppliances: {
      cooling: ['fan_regular', 'ac_noninv_1_5', 'fan_table'],
      lighting: ['led_9', 'led_18', 'tube_light'],
      kitchen: ['fridge', 'geyser', 'rice_cooker'],
      entertainment: ['tv_led', 'desktop_pc', 'router'],
      water: ['washer', 'pump_1hp'],
      personal: ['hair_dryer', 'shaver_electric'],
      other: ['iron', 'vacuum_cleaner']
    },

    // Household-size starting points for the Step 2 appliance pre-fill.
    // Only `medium` is used (ec-appliances.js DEFAULT_PRESET) — no Step 1
    // question asks the visitor's family size any more. `small`/`large` stay
    // defined as real data and stay covered by test/core.test.js.
    // Shape: { key, qty, hours } where hours = hours of use per day as the user
    // would describe them (duty cycle is applied on top by the math layer).
    familyPresets: {
      // ২-৩ জন — smaller flat, typically no AC.
      small: [
        { key: 'fan_regular',   qty: 2, hours: 12 },
        { key: 'led_9',         qty: 4, hours: 6 },
        { key: 'fridge',        qty: 1, hours: 24 },
        { key: 'tv_led',        qty: 1, hours: 4 }
      ],
      // ৪-৫ জন — the default landing case (PROJECT.md §3).
      medium: [
        { key: 'ac_noninv_1_5', qty: 1, hours: 8 },
        { key: 'fan_regular',   qty: 4, hours: 12 },
        { key: 'led_9',         qty: 6, hours: 6 },
        { key: 'fridge',        qty: 1, hours: 24 },
        { key: 'tv_led',        qty: 1, hours: 5 }
      ],
      // ৬+ জন — more rooms, second AC, geyser and washing machine in use.
      large: [
        { key: 'ac_noninv_1_5', qty: 2, hours: 8 },
        { key: 'fan_regular',   qty: 6, hours: 14 },
        { key: 'led_9',         qty: 10, hours: 6 },
        { key: 'fridge',        qty: 1, hours: 24 },
        { key: 'tv_led',        qty: 2, hours: 6 },
        { key: 'geyser',        qty: 1, hours: 1 },
        { key: 'washer',        qty: 1, hours: 1 }
      ]
    }
  };

  root.EC_RATES = EC_RATES;
  if (typeof module !== 'undefined' && module.exports) module.exports = EC_RATES;
})(typeof globalThis !== 'undefined' ? globalThis : this);
